export class InputState {
  private keys = new Set<string>();
  private touches = new Set<string>();
  private jumpQueued = false;
  private jumpReleased = false;
  private gamepadLeft = false;
  private gamepadRight = false;
  private gamepadRun = false;
  private gamepadJump = false;
  private gamepadPause = false;
  private gamepadHorizontal = 0;
  private gamepadVertical = 0;

  constructor(
    private readonly onPause: () => void,
    private readonly onPrimary: () => void,
    private readonly onNavigate: (direction: -1 | 1) => void,
  ) {
    window.addEventListener("keydown", (event) => {
      if (["ArrowLeft", "ArrowRight", "ArrowUp", "Space"].includes(event.code)) {
        event.preventDefault();
      }
      if (!event.repeat && this.isJumpCode(event.code)) this.jumpQueued = true;
      if (!event.repeat && (event.code === "Escape" || event.code === "KeyP")) this.onPause();
      this.keys.add(event.code);
    });

    window.addEventListener("keyup", (event) => {
      this.keys.delete(event.code);
      if (this.isJumpCode(event.code)) this.jumpReleased = true;
    });

    window.addEventListener("blur", () => this.clear());

    document.querySelectorAll<HTMLButtonElement>("[data-control]").forEach((button) => {
      const control = button.dataset.control;
      if (!control) return;

      const press = (event: PointerEvent) => {
        event.preventDefault();
        button.setPointerCapture(event.pointerId);
        if (control === "jump" && !this.touches.has(control)) this.jumpQueued = true;
        this.touches.add(control);
        button.classList.add("is-pressed");
      };
      const release = (event: PointerEvent) => {
        event.preventDefault();
        if (control === "jump" && this.touches.has(control)) this.jumpReleased = true;
        this.touches.delete(control);
        button.classList.remove("is-pressed");
      };

      button.addEventListener("pointerdown", press);
      button.addEventListener("pointerup", release);
      button.addEventListener("pointercancel", release);
      button.addEventListener("lostpointercapture", release);
    });
  }

  get left(): boolean {
    return this.keys.has("ArrowLeft") || this.keys.has("KeyA") || this.touches.has("left") || this.gamepadLeft;
  }

  get right(): boolean {
    return this.keys.has("ArrowRight") || this.keys.has("KeyD") || this.touches.has("right") || this.gamepadRight;
  }

  get run(): boolean {
    return this.keys.has("ShiftLeft") || this.keys.has("ShiftRight") || this.touches.has("run") || this.gamepadRun;
  }

  get jumpHeld(): boolean {
    return (
      this.keys.has("ArrowUp") ||
      this.keys.has("KeyW") ||
      this.keys.has("Space") ||
      this.touches.has("jump") ||
      this.gamepadJump
    );
  }

  pollGamepad(): void {
    const pads = navigator.getGamepads?.() ?? [];
    const pad = Array.from(pads).find((candidate): candidate is Gamepad => Boolean(candidate?.connected));
    if (!pad) {
      this.gamepadLeft = false;
      this.gamepadRight = false;
      this.gamepadRun = false;
      this.gamepadJump = false;
      this.gamepadPause = false;
      this.gamepadHorizontal = 0;
      this.gamepadVertical = 0;
      return;
    }

    const axisX = Math.abs(pad.axes[0] ?? 0) > 0.24 ? (pad.axes[0] ?? 0) : 0;
    const axisY = Math.abs(pad.axes[1] ?? 0) > 0.42 ? (pad.axes[1] ?? 0) : 0;
    const horizontal = pad.buttons[14]?.pressed || axisX < -0.45
      ? -1
      : pad.buttons[15]?.pressed || axisX > 0.45
        ? 1
        : 0;
    const vertical = pad.buttons[12]?.pressed || axisY < -0.55
      ? -1
      : pad.buttons[13]?.pressed || axisY > 0.55
        ? 1
        : 0;
    const jump = Boolean(pad.buttons[0]?.pressed || pad.buttons[1]?.pressed);
    const pause = Boolean(pad.buttons[9]?.pressed);

    if (jump && !this.gamepadJump) {
      this.jumpQueued = true;
      this.onPrimary();
    } else if (!jump && this.gamepadJump) {
      this.jumpReleased = true;
    }
    if (pause && !this.gamepadPause) this.onPause();
    if (horizontal !== 0 && horizontal !== this.gamepadHorizontal) this.onNavigate(horizontal as -1 | 1);
    if (vertical !== 0 && vertical !== this.gamepadVertical) this.onNavigate(vertical as -1 | 1);

    this.gamepadLeft = horizontal < 0;
    this.gamepadRight = horizontal > 0;
    this.gamepadRun = Boolean(pad.buttons[2]?.pressed || pad.buttons[5]?.pressed || (pad.buttons[7]?.value ?? 0) > 0.35);
    this.gamepadJump = jump;
    this.gamepadPause = pause;
    this.gamepadHorizontal = horizontal;
    this.gamepadVertical = vertical;
  }

  consumeJump(): boolean {
    const queued = this.jumpQueued;
    this.jumpQueued = false;
    return queued;
  }

  consumeJumpRelease(): boolean {
    const released = this.jumpReleased;
    this.jumpReleased = false;
    return released;
  }

  clear(): void {
    this.keys.clear();
    this.touches.clear();
    this.jumpQueued = false;
    this.jumpReleased = false;
    this.gamepadLeft = false;
    this.gamepadRight = false;
    this.gamepadRun = false;
    this.gamepadJump = false;
    this.gamepadPause = false;
    this.gamepadHorizontal = 0;
    this.gamepadVertical = 0;
    document.querySelectorAll("[data-control]").forEach((button) => button.classList.remove("is-pressed"));
  }

  private isJumpCode(code: string): boolean {
    return code === "ArrowUp" || code === "KeyW" || code === "Space";
  }
}
